/**
 * Shared tracking utilities — used by both the tracking widget (client)
 * and the my-shipments pages (server).
 */

// ISO 6346 size/type code decoder
// Char 1: length (2=20', 4=40', L=45', M=48')
// Char 2: height (0/2/3=standard 8'6", 5/6=9'6" high cube)
// Char 3: type (G=GP/DV, R=Reefer, U=Open Top, P=Flat Rack, T=Tank)

const ISO_LENGTH: Record<string, string> = {
  '1': "10'", '2': "20'", '3': "30'", '4': "40'",
  'L': "45'", 'M': "48'", 'N': "49'",
};

const ISO_HEIGHT_HC = new Set(['5', '6', '9', 'D', 'E']);

const ISO_TYPE: Record<string, string> = {
  'G': 'Dry Van', 'GP': 'Dry Van',
  'R': 'Reefer', 'RE': 'Reefer', 'RS': 'Reefer',
  'U': 'Open Top', 'UT': 'Open Top',
  'P': 'Flat Rack', 'PL': 'Flat Rack', 'PC': 'Flat Rack Collapsible',
  'T': 'Tank', 'TN': 'Tank', 'TG': 'Tank',
  'B': 'Bulk', 'BU': 'Bulk', 'BK': 'Bulk',
  'V': 'Ventilated', 'VH': 'Ventilated',
  'H': 'Insulated',
  'S': 'Livestock',
};

export function decodeISOEquipment(code: string | undefined): string | null {
  if (!code || code.length < 2) return null;

  const lengthChar = code.charAt(0);
  const heightChar = code.charAt(1);
  const length = ISO_LENGTH[lengthChar];
  if (!length) return code;

  const isHighCube = ISO_HEIGHT_HC.has(heightChar);

  if (/^\d+$/.test(code)) {
    return `${length}${isHighCube ? ' High Cube' : ''}`;
  }

  const typeStr = code.slice(2);
  const typeChar = typeStr.charAt(0);
  const type = ISO_TYPE[typeStr.slice(0, 2)] ?? ISO_TYPE[typeChar] ?? '';

  const parts = [length];
  if (isHighCube) parts.push('High Cube');
  if (type) parts.push(type);
  return parts.join(' ');
}

export const EVENT_LABELS: Record<string, string> = {
  DEPA: 'Departed',
  ARRI: 'Arrived',
  LOAD: 'Loaded on vessel',
  DISC: 'Discharged from vessel',
  GTIN: 'Gate in',
  GTOT: 'Gate out',
  STUF: 'Container stuffed',
  STRP: 'Container stripped',
  RECE: 'Received',
  DRFT: 'Draft',
  PENA: 'Pending approval',
  CONF: 'Confirmed',
  ISSU: 'Issued',
  APPR: 'Approved',
  SURR: 'Surrendered',
  CMPL: 'Completed',
  VOID: 'Voided',
};

export interface SnapshotEvent {
  eventType?: string;
  eventClassifierCode?: string;
  eventDateTime?: string;
  transportEventTypeCode?: string;
  transportCallSequenceNumber?: number;
  modeOfTransport?: string;
  equipmentEventTypeCode?: string;
  shipmentEventTypeCode?: string;
  UNLocationCode?: string;
  locationName?: string;
  vesselName?: string;
  voyageNumber?: string;
  equipmentReference?: string;
  ISOEquipmentCode?: string;
  emptyIndicatorCode?: string;
  description?: string;
  [key: string]: unknown;
}

export function eventLabel(e: SnapshotEvent): string {
  const code = e.transportEventTypeCode ?? e.equipmentEventTypeCode ?? e.shipmentEventTypeCode ?? '';
  return EVENT_LABELS[code] ?? code;
}

export interface ContainerInfo {
  reference: string;
  isoCode?: string;
  decoded?: string;
}

export function extractContainersFromEvents(events: SnapshotEvent[]): ContainerInfo[] {
  const map = new Map<string, ContainerInfo>();
  for (const e of events) {
    if (e.equipmentReference && !map.has(e.equipmentReference)) {
      map.set(e.equipmentReference, {
        reference: e.equipmentReference,
        isoCode: e.ISOEquipmentCode,
        decoded: decodeISOEquipment(e.ISOEquipmentCode) ?? undefined,
      });
    }
  }
  return [...map.values()];
}

export function groupContainersByType(containers: ContainerInfo[]): { label: string; count: number }[] {
  const groups = new Map<string, number>();
  for (const c of containers) {
    const key = c.decoded ?? c.isoCode ?? 'Unknown';
    groups.set(key, (groups.get(key) ?? 0) + 1);
  }
  return [...groups.entries()].map(([label, count]) => ({ label, count }));
}

export function getLatestActualEvent(events: SnapshotEvent[]): SnapshotEvent | null {
  const actual = events.filter((e) => e.eventClassifierCode === 'ACT');
  if (actual.length === 0) return null;
  return actual.reduce((latest, e) =>
    (e.eventDateTime ?? '') > (latest.eventDateTime ?? '') ? e : latest
  );
}

export function findArrivalAtDestination(
  events: SnapshotEvent[],
  destinationUnLocode: string | null | undefined,
): SnapshotEvent | null {
  let destLoc = destinationUnLocode ?? null;
  if (!destLoc) {
    const route = seaRouteEvents(events);
    destLoc = route[route.length - 1]?.UNLocationCode ?? null;
  }
  if (!destLoc) return null;

  const matches = events.filter(
    (e) =>
      e.eventClassifierCode === 'ACT' &&
      e.UNLocationCode === destLoc &&
      (e.transportEventTypeCode === 'ARRI' ||
        e.equipmentEventTypeCode === 'DISC' ||
        e.equipmentEventTypeCode === 'GTIN'),
  );
  if (matches.length === 0) return null;
  return matches.reduce((earliest, e) =>
    (e.eventDateTime ?? '') < (earliest.eventDateTime ?? '') ? e : earliest,
  );
}

/** Sea-route ordering must not depend on response order or undated inland moves.
 * CMA supplies an explicit shipment call sequence; other carriers fall back to
 * timestamp order. Never compare sequence numbers from different carriers.
 */
export function seaRouteEvents<T extends SnapshotEvent>(events: T[]): T[] {
  const transport = events.filter(e => e.eventType === 'TRANSPORT' && (e.UNLocationCode || e.locationName));
  const sea = transport.filter(e => e.modeOfTransport === 'VESSEL');
  const candidates = sea.length ? sea : transport.filter(e => !e.modeOfTransport);
  const sequence = candidates.length > 0 && candidates.every(e => Number.isFinite(e.transportCallSequenceNumber))
    && new Set(candidates.map(e => e.carrier)).size <= 1;
  return candidates.filter(e => sequence || Number.isFinite(Date.parse(e.eventDateTime ?? '')))
    .slice().sort((a, b) => sequence
      ? a.transportCallSequenceNumber! - b.transportCallSequenceNumber!
      : Date.parse(a.eventDateTime!) - Date.parse(b.eventDateTime!));
}

/** Final inland delivery is separate from the port of discharge. Only infer it
 * when the carrier explicitly orders an inland arrival after the sea route.
 */
export function finalDeliveryEvent<T extends SnapshotEvent>(events: T[]): T | null {
  const sea = seaRouteEvents(events);
  const last = sea[sea.length - 1];
  if (!last || !Number.isFinite(last.transportCallSequenceNumber)) return null;
  return events.filter(e => e.carrier === last.carrier && e.eventType === 'TRANSPORT'
    && e.transportEventTypeCode === 'ARRI' && ['TRUCK', 'RAIL', 'BARGE'].includes(e.modeOfTransport ?? '')
    && Number.isFinite(e.transportCallSequenceNumber)
    && e.transportCallSequenceNumber! > last.transportCallSequenceNumber!)
    .sort((a, b) => b.transportCallSequenceNumber! - a.transportCallSequenceNumber!)[0] ?? null;
}

export function extractRouteEndpoints(events: SnapshotEvent[]): { origin: string | null; destination: string | null } {
  const route = seaRouteEvents(events);
  const first = route[0];
  const last = route[route.length - 1];
  return { origin: first?.locationName ?? first?.UNLocationCode ?? null,
    destination: last?.locationName ?? last?.UNLocationCode ?? null };
}

export function extractRouteUnlocodes(events: SnapshotEvent[]): { origin: string | null; destination: string | null } {
  const route = seaRouteEvents(events);
  return { origin: route[0]?.UNLocationCode ?? null, destination: route[route.length - 1]?.UNLocationCode ?? null };
}

/** Display the offset-local clock supplied by the carrier, not the viewer's zone. */
export function formatCarrierDate(iso: string | undefined, includeTime = true): string {
  if (!iso || !Number.isFinite(Date.parse(iso))) return 'Date not supplied';
  const local = new Date(iso.slice(0, 19) + 'Z');
  return local.toLocaleString('en-GB', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } as const : {}) });
}
